import { ArrowRightOutlined, EditOutlined } from "@ant-design/icons";
import { Button, Col, Row, Space, Typography } from "antd";
import Table, { ColumnType } from "antd/lib/table";
import { encode } from "querystring";
import {
  FunctionComponent,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useStateIfMounted } from "use-state-if-mounted";
import ActionBtn from "../../../shared/action-btn";
import {
  ROUTE_DASHBOARD_USER_DETAIL,
  ROUTE_DASHBOARD_USER_EDIT,
} from "../../../shared/app-constants";
import DeleteBtn from "../../../shared/delete-btn";
import getDashboardRoute from "../../../shared/get-dashboard-route";
import GuardedElement from "../../../shared/guarded-element";
import Permissions from "../../../shared/permissions";
import RejectBtn from "../../../shared/reject-btn";
import uDayjs from "../../../shared/uDayjs";
import UserModel from "../../../shared/user-model";
import UserStatus from "../../../shared/user-status";
import ListFilter from "../../components/list-filter";
import RoleSelector from "../../components/role-selector";
import UserStatusSelector from "../../components/user-status-selector";
import { AppContext } from "../../contexts/app-context";
import HttpClient from "../../http-client";
interface UserListPageProps {
  self?: boolean;
}

const UserListPage: FunctionComponent<UserListPageProps> = ({ self }) => {
  const [list, setList] = useStateIfMounted<UserModel[]>([]);
  const [busy, setBusy] = useStateIfMounted(false);
  const [hasMore, setHasMore] = useStateIfMounted(false);
  const { user } = useContext(AppContext);
  const [search] = useSearchParams();
  const loadMore = useRef<() => Promise<void>>();
  const [savingList, setSavingList] = useStateIfMounted<string[]>([]);

  const remove = (id: string) => {
    setList((list) =>
      list.filter((each) => {
        return each.id !== id;
      })
    );
  };

  const saveUser = useCallback((id: string, data: Partial<UserModel>) => {
    setSavingList((list) => [...list, id]);
    HttpClient.patch(`user/${id}`, data)
      .then((res) => {
        if (res.data?.data) {
          setList((list) =>
            list.map((each) => {
              if (each.id === id) {
                return { ...each, ...res.data.data };
              }
              return each;
            })
          );
        }
      })
      .finally(() => {
        setSavingList((list) => list.filter((each) => each !== id));
      });
  }, []);

  const columns: ColumnType<UserModel>[] = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (val, record) => (
        <Link
          to={{
            pathname: getDashboardRoute(
              { id: record.id! },
              ROUTE_DASHBOARD_USER_DETAIL
            ),
          }}
        >
          {val}
        </Link>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      render: (val, rec) => (
        <RoleSelector
          value={val}
          disabled={savingList.includes(rec.id!)}
          onChange={(data) =>
            saveUser(rec.id!, {
              ...rec,
              role_id: data?.id,
            })
          }
        />
      ),
    },
    {
      title: "Status",
      dataIndex: "user_status",
      key: "user_status",
      render: (val, rec) => (
        <UserStatusSelector
          value={val}
          disabled={savingList.includes(rec.id!)}
          onChange={(data) =>
            saveUser(rec.id!, {
              ...rec,
              user_status: data,
            })
          }
        />
      ),
    },
    {
      title: "Updated at",
      dataIndex: "updated_at",
      key: "updated_at",
      render: (value) =>
        value ? uDayjs(value).format("DD/MM/YYYY hh:mma") : null,
    },
    {
      title: "Actions",
      key: "id",
      dataIndex: "id",
      render: (id, user) => (
        <Space>
          <Button type="default" size="small" icon={<ArrowRightOutlined />}>
            <Link
              to={{
                pathname: getDashboardRoute(
                  { id },
                  ROUTE_DASHBOARD_USER_DETAIL
                ),
              }}
            >
              Detail
            </Link>
          </Button>
          <GuardedElement permissions={[Permissions.EDIT_USER]}>
            <Button type="default" size="small" icon={<EditOutlined />}>
              <Link
                to={{
                  pathname: getDashboardRoute(
                    { id },
                    ROUTE_DASHBOARD_USER_EDIT
                  ),
                }}
              >
                Edit
              </Link>
            </Button>
          </GuardedElement>
          <GuardedElement permissions={[Permissions.DELETE_USER]}>
            <DeleteBtn
              path={`user/${id}`}
              httpClient={HttpClient}
              onDone={() => remove(id)}
            />
          </GuardedElement>
          {
            user.user_status === UserStatus.Pending && <GuardedElement permissions={[Permissions.DELETE_USER]}>

              <RejectBtn
                path={`user/${id}/reject`}
                httpClient={HttpClient}
                onDone={() => remove(id)}
              />
            </GuardedElement>
          }
        </Space>
      ),
    },
  ];

  useEffect(() => {
    setList([]);
    let page = 0;
    loadMore.current = async () => {
      page++;
      setBusy(true);
      try {
        const query: any = { page };

        if (search.get("query")) {
          query.name = search.get("query");
        }

        const qs = encode(query);
        const { data } = await HttpClient.get(`user?${qs}`);
        if (Array.isArray(data?.data)) {
          setList((list) => [...list, ...data.data]);
        }
        setHasMore(!!data?.meta?.nextPage);
      } catch (e) {
        console.log(e);
      }

      setBusy(false);
    };
    loadMore.current();
  }, [search, self, user]);
  return (
    <Space direction="vertical">
      <Row>
        <Col xs={12} md={16}>
          <Typography.Title level={4}>Users</Typography.Title>
        </Col>
        <Col xs={12} md={8}>
          <ListFilter />
        </Col>
      </Row>
      <Table
        columns={columns}
        loading={busy}
        dataSource={list}
        rowKey={"id"}
        pagination={false}
        footer={() => {
          if (hasMore) {
            return (
              <div className="tc">
                <ActionBtn busy={busy} onClick={loadMore.current}>
                  Load More
                </ActionBtn>
              </div>
            );
          }
          return null;
        }}
      />
    </Space>
  );
};

export default UserListPage;
